import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AuthUser,
  LoginResponse,
  TotpVerifyResponse,
  RecoveryVerifyResponse,
} from '@mdu-invoicing/shared';
import { apiFetch, setAccessToken, ApiError } from '@/lib/api-client';

// ── Auth state machine ────────────────────────────────────────
// Wired to the real API (ported from Uniticket, adapted for MDU). MDU keeps a
// simplified machine: providers are provisioned with TOTP already enabled, so
// the TOTP_SETUP / PASSWORD_EXPIRED steps are not exposed in the UI yet.

type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'totp_verify'; tempToken: string }
  | { status: 'authenticated'; user: AuthUser };

interface AuthContextType {
  state: AuthState;
  user: AuthUser | null;
  isStaff: boolean;
  isProvider: boolean;
  /** Subco ADMIN (a PROVIDER whose subRole is ADMIN). */
  isSubcoAdmin: boolean;
  /** May manage accounts — STAFF (all contractors) or a subco ADMIN (its own). Gates the
   *  account-management screen + nav. */
  canManageUsers: boolean;
  /** May this user see monetary amounts? (server-enforced; the UI also gates on it) */
  canViewAmounts: boolean;
  login: (email: string, password: string) => Promise<void>;
  totpVerify: (code: string, rememberDevice?: boolean) => Promise<void>;
  recoveryVerify: (code: string, rememberDevice?: boolean) => Promise<number>;
  ssoLogin: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// ── Helper: fetch with temp token (during the 2FA step) ───────

async function tempFetch<T>(path: string, tempToken: string, body?: unknown): Promise<T> {
  const res = await fetch(`/internal/v1${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tempToken}`,
    },
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({ message: res.statusText }));
    throw new ApiError(res.status, data.message || res.statusText);
  }

  return res.json() as Promise<T>;
}

// ── AuthProvider ──────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const user = state.status === 'authenticated' ? state.user : null;

  // Sync i18n language with the user preference on login / session restore.
  const { i18n } = useTranslation();
  useEffect(() => {
    if (user?.languagePref) {
      i18n.changeLanguage(user.languagePref);
    }
  }, [user?.languagePref, i18n]);

  // On mount: attempt silent refresh to restore the session.
  useEffect(() => {
    let cancelled = false;

    async function tryRestore() {
      try {
        const res = await fetch('/internal/v1/auth/refresh', {
          method: 'POST',
          credentials: 'include',
        });

        if (!res.ok) {
          if (!cancelled) setState({ status: 'unauthenticated' });
          return;
        }

        const { accessToken } = await res.json();
        setAccessToken(accessToken);

        const { user: authUser } = await apiFetch<{ user: AuthUser }>('/auth/me');
        if (!cancelled) setState({ status: 'authenticated', user: authUser });
      } catch {
        if (!cancelled) setState({ status: 'unauthenticated' });
      }
    }

    tryRestore();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── login (password) ──────────────────────────────────────

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch('/internal/v1/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
      credentials: 'include',
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({ message: res.statusText }));
      throw new ApiError(res.status, data.message || res.statusText);
    }

    const data: LoginResponse = await res.json();

    if (data.step === 'AUTHENTICATED') {
      setAccessToken(data.accessToken);
      setState({ status: 'authenticated', user: data.user });
    } else if (data.step === 'TOTP_VERIFY') {
      setState({ status: 'totp_verify', tempToken: data.tempToken });
    } else {
      // TOTP_SETUP / PASSWORD_EXPIRED are not surfaced in Phase 0 (providers are
      // provisioned with TOTP already enabled). Fail with a clear message.
      throw new ApiError(
        400,
        'This account needs additional setup that is not available yet. Please contact your administrator.',
      );
    }
  }, []);

  // ── totpVerify ────────────────────────────────────────────

  const totpVerify = useCallback(
    async (code: string, rememberDevice?: boolean) => {
      if (state.status !== 'totp_verify') throw new Error('Not in TOTP verify state');

      const data = await tempFetch<TotpVerifyResponse>('/auth/totp/verify', state.tempToken, {
        code,
        rememberDevice,
      });

      setAccessToken(data.accessToken);
      setState({ status: 'authenticated', user: data.user });
    },
    [state],
  );

  // ── recoveryVerify ────────────────────────────────────────

  const recoveryVerify = useCallback(
    async (code: string, rememberDevice?: boolean): Promise<number> => {
      if (state.status !== 'totp_verify') throw new Error('Not in TOTP verify state');

      const data = await tempFetch<RecoveryVerifyResponse>(
        '/auth/recovery/verify',
        state.tempToken,
        { code, rememberDevice },
      );

      setAccessToken(data.accessToken);
      setState({ status: 'authenticated', user: data.user });
      return data.remainingCodes;
    },
    [state],
  );

  // ── ssoLogin (Microsoft Entra — staff) ───────────────────

  const ssoLogin = useCallback(async () => {
    // Full-page redirect into the Entra auth-code flow; returns to the app on success.
    window.location.href = '/internal/v1/auth/sso/login';
  }, []);

  // ── logout ────────────────────────────────────────────────

  const logout = useCallback(async () => {
    const wasEntra = user?.authType === 'ENTRA_ID';
    try {
      await fetch('/internal/v1/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Ignore — we're logging out anyway.
    }
    setAccessToken(null);
    setState({ status: 'unauthenticated' });

    if (wasEntra) {
      window.location.href = '/internal/v1/auth/sso/logout';
    }
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        state,
        user,
        isStaff: user?.role === 'STAFF',
        isProvider: user?.role === 'PROVIDER',
        isSubcoAdmin: user?.role === 'PROVIDER' && user?.subRole === 'ADMIN',
        canManageUsers: user?.role === 'STAFF' || (user?.role === 'PROVIDER' && user?.subRole === 'ADMIN'),
        canViewAmounts: !!user?.canViewAmounts,
        login,
        totpVerify,
        recoveryVerify,
        ssoLogin,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
